import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Wordmark } from '../ui'
import { attorneyApi } from '../api'

// Admin bar-verification page, reached from the email link (/verify/:token).
// Public — the signed token is the authorization. Confirm on the CO registry,
// then mark Verified (goes live) or Not verified (stays out of the directory).
const CO_SEARCH = 'https://www.coloradolegalregulation.com/attorney-search/'

function Shell({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 font-sans text-slate-900">
      <header className="border-b border-slate-200 bg-white/90 px-5 py-3.5"><Wordmark /></header>
      <main className="flex flex-1 items-center justify-center px-5 py-10">
        <div className="w-full max-w-md">{children}</div>
      </main>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-900">{value || '—'}</dd>
    </div>
  )
}

export function VerifyAttorney() {
  const { token } = useParams()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState('')

  useEffect(() => {
    attorneyApi.verifyLookup(token).then(setData)
      .catch((e) => setError(e.message || 'This verification link is invalid or expired.'))
  }, [token])

  const decide = async (action) => {
    setBusy(action); setError('')
    try { setResult(await attorneyApi.verifyDecide(token, action)) }
    catch (e) { setError(e.message || 'Could not save your decision.') }
    setBusy('')
  }

  if (error && !data) {
    return <Shell><div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      <p className="text-sm text-slate-600">{error}</p></div></Shell>
  }
  if (!data) return <Shell><p className="text-center text-sm text-slate-500">Loading…</p></Shell>

  if (result) {
    const verified = result.verification === 'verified'
    return <Shell><div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full text-2xl ${verified ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{verified ? '✓' : '—'}</div>
      <h1 className="text-xl font-bold tracking-tight">{verified ? `${data.name} is verified` : `${data.name} marked not verified`}</h1>
      <p className="mt-2 text-sm text-slate-600">{verified
        ? 'Their profile is now live in the directory.'
        : 'Their profile stays out of the directory.'}</p>
    </div></Shell>
  }

  return <Shell>
    <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
      <h1 className="text-xl font-bold tracking-tight">Verify attorney</h1>
      <p className="mt-1 text-sm text-slate-500">Confirm this attorney's Colorado bar registration before their profile goes live.</p>
      <dl className="mt-6 space-y-2 text-sm">
        <Row label="Name" value={data.name} />
        <Row label="Firm" value={data.firm} />
        <Row label="Bar number" value={data.bar_number} />
        <Row label="Email" value={data.email} />
      </dl>
      <a href={CO_SEARCH} target="_blank" rel="noopener noreferrer"
        className="mt-5 inline-block text-sm font-semibold text-emerald-700 hover:underline">Look up on the Colorado registry ↗</a>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-7 flex flex-wrap gap-3">
        <button onClick={() => decide('verify')} disabled={!!busy}
          className="flex-1 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-3 text-sm font-bold text-white shadow transition hover:-translate-y-0.5 disabled:opacity-60">
          {busy === 'verify' ? 'Saving…' : 'Verified'}
        </button>
        <button onClick={() => decide('reject')} disabled={!!busy}
          className="flex-1 rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60">
          {busy === 'reject' ? 'Saving…' : 'Not verified'}
        </button>
      </div>
    </div>
  </Shell>
}

export default VerifyAttorney
